import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useOnboarding, type TaskKey } from '../hooks/useOnboarding';
import { isTourSeen } from './tourSeen';
import { getTourDefinition } from './tourDefinitions';

/**
 * Hook contestuale: rileva quando l'utente entra in una pagina coperta da un tour
 * (es. /deals, /automation, /settings?tab=company) e, se non l'ha mai visto e
 * non ha completato il task, aggiunge ?tour=<taskId> alla URL. `useDriverTour`
 * (montato in parallelo) intercetta il param e lancia il tour.
 *
 * Filosofia: "non lasciarlo solo". Anche se l'utente non clicca dalla checklist,
 * la prima volta che mette piede in una sezione viene accompagnato.
 *
 * Trigger condizionale:
 * - localStorage flag `flusia-tour-seen-<taskId>` non presente
 * - task della checklist non gia done (se done, l'utente conosce la sezione)
 * - non c'e gia un `?tour=...` in URL (no doppi trigger)
 * - sessionStorage flag per evitare retrigger immediato dopo close del tour
 *
 * Aggiungere nuovi mapping qui sotto in PATH_RULES.
 */
interface PathRule {
  taskId: TaskKey;
  /** Restituisce true se la pathname+search corrispondono al target del tour. */
  match: (pathname: string, search: URLSearchParams) => boolean;
}

// NB: elencare qui SOLO taskId che hanno una TourDefinition (getTourDefinition
// non-null). Le regole 'deals'/'automations' sono state rimosse perché prive di
// definizione: impostavano ?tour=... a vuoto bruciando il flag senza mostrare nulla.
const PATH_RULES: PathRule[] = [
  { taskId: 'pipeline',     match: (p, s) => p === '/settings' && s.get('tab') === 'lead-pipelines' },
  { taskId: 'goal',         match: (p, s) => p === '/settings' && s.get('tab') === 'sales-targets' },
  { taskId: 'integrations', match: (p, s) => p === '/settings' && s.get('tab') === 'integrations-hub' },
  { taskId: 'profile',      match: (p, s) => p === '/settings' && s.get('tab') === 'company' },
  { taskId: 'team',         match: (p, s) => p === '/settings' && s.get('tab') === 'users' },
  { taskId: 'lead',         match: (p) => p === '/leads' },
  { taskId: 'referral',     match: (p) => p === '/referrals' },
];

const SESSION_FIRED_KEY = (taskId: string) => `flusia-tour-fired-${taskId}`;

export function useContextualTourTrigger(enabled = true): void {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { checklist } = useOnboarding();
  const lastFiredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    // Se c'e gia un ?tour=... attivo, non interferire
    if (searchParams.get('tour')) return;

    const rule = PATH_RULES.find((r) => r.match(location.pathname, searchParams));
    if (!rule) return;

    // Difesa: non triggerare tour privi di definizione (nessun popup, solo flag bruciato)
    if (!getTourDefinition(rule.taskId)) return;

    // Stesso task gia firato in questa render → skip
    if (lastFiredRef.current === rule.taskId) return;

    // Task gia visto (flag scritto SOLO a tour concluso, vedi tourSeen) /
    // gia firato in questa sessione → skip
    if (isTourSeen(rule.taskId)) return;
    try {
      if (sessionStorage.getItem(SESSION_FIRED_KEY(rule.taskId)) === '1') return;
    } catch {
      // sessionStorage non disponibile (private mode) → procedi comunque
    }

    const taskDone = checklist?.tasks?.[rule.taskId]?.done === true;
    if (taskDone) return;

    // Marca "firato in questa sessione" (solo sessionStorage: evita il retrigger
    // immediato nella stessa sessione se il tour non parte, ma NON è permanente,
    // così alla prossima sessione — o quando l'elemento sarà montato — si ritenta.
    // Il flag PERMANENTE 'seen' lo scrive useDriverTour solo a tour concluso).
    try {
      sessionStorage.setItem(SESSION_FIRED_KEY(rule.taskId), '1');
    } catch {
      // ignore
    }
    lastFiredRef.current = rule.taskId;

    // Defer di 400ms per dare tempo alla pagina di renderizzare i data-tour markers
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(window.location.search);
      next.set('tour', rule.taskId);
      setSearchParams(next, { replace: true });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [enabled, location.pathname, searchParams, checklist, setSearchParams]);
}
